import clsx from 'clsx';
import {ReactNode, forwardRef, useEffect, useImperativeHandle, useState} from 'react';

/**
 * Props for the Modal component
 */
type ModalPropsT = {
  title: string;
  children: ReactNode;
  error?: string | null;
  isSubmitting?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  clickToClose?: boolean;
  closeButtonLabel?: string;
  onClose?: () => void;
};

/**
 * Ref type for controlling the Modal externally
 * @example
 * ```tsx
 * const modalRef = useRef<ModalRefT>(null);
 *
 * // Show the modal
 * modalRef.current?.show();
 *
 * // Hide the modal
 * modalRef.current?.hide();
 * ```
 */
export type ModalRefT = {
  show: () => void;
  hide: () => void;
  isVisible: boolean;
};

/**
 * Internal component that renders the modal content
 */
const ModalComponent = ({
  children, // The modal body content
  title, // The modal title
  error, // The modal error message
  isSubmitting, // Whether the modal is submitting
  isVisible, // Whether the modal is visible
  size = 'md', // The modal size
  clickToClose = true, // Whether the modal can be closed by clicking on the modal content
  closeButtonLabel, // The label for the close button
  onClose, // The callback function when the modal is closed
  setIsVisible, // The callback function to set the modal visibility
}: ModalPropsT & {
  isVisible: boolean;
  setIsVisible: (value: boolean) => void;
}) => {
  // Add/remove overflow-hidden class on body when modal visibility changes
  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    // Cleanup when component unmounts
    return () => {
      document.body.style.overflow = '';
    };
  }, [isVisible]);

  const handleClose = () => {
    setIsVisible(false);
    onClose?.();
  };

  return (
    <div
      className={clsx(
        'ti-modal fixed inset-0 z-[99999] items-center justify-center overflow-hidden bg-black/30 backdrop-blur-sm',
        isVisible ? 'flex' : 'hidden'
      )}
      style={{position: 'fixed', zIndex: 99999}}
      onClick={clickToClose ? handleClose : undefined}
    >
      <div
        className={clsx(
          'relative m-3 max-h-[90vh] w-full transform overflow-hidden transition-all duration-300 sm:mx-auto sm:w-full sm:max-w-md md:max-w-lg',
          size === 'sm' && 'sm:max-w-sm',
          size === 'md' && 'md:max-w-lg',
          size === 'lg' && 'lg:max-w-xl',
          size === 'xl' && 'xl:max-w-2xl'
        )}
        onClick={e => e.stopPropagation()}
      >
        <div className="ti-modal-content">
          <div className="ti-modal-header">
            <h6 className="ti-modal-title">{title}</h6>
            <button type="button" className="ti-modal-close-btn mr-4" onClick={handleClose} disabled={isSubmitting}>
              <span
                className={clsx({
                  'sr-only': !closeButtonLabel,
                  'mr-2': closeButtonLabel,
                })}
              >
                {closeButtonLabel ? closeButtonLabel : 'Close'}
              </span>
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
          {error && (
            <div className="alert-danger mb-2 px-4 py-3 text-sm shadow-md" role="alert">
              <div className="flex items-center">
                <i className="ri-error-warning-line mr-2 text-lg"></i>
                <div>{error}</div>
              </div>
            </div>
          )}
          <div className="ti-modal-body flex-1 overflow-y-auto">{children}</div>
        </div>
      </div>
    </div>
  );
};

/**
 * A reusable Modal component with imperative handle for showing/hiding
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const modalRef = useRef<ModalRefT>(null);
 *
 *   return (
 *     <>
 *       <button onClick={() => modalRef.current?.show()}>
 *         Open Modal
 *       </button>
 *
 *       <Modal
 *         ref={modalRef}
 *         title="My Modal"
 *         onClose={() => console.log('Modal closed')}
 *       >
 *         <div>Modal content goes here</div>
 *       </Modal>
 *     </>
 *   );
 * }
 * ```
 */
export const Modal = forwardRef<ModalRefT, ModalPropsT>((props, ref) => {
  const [isVisible, setIsVisible] = useState(false);

  useImperativeHandle(ref, () => ({
    show: () => setIsVisible(true),
    hide: () => setIsVisible(false),
    isVisible,
  }));

  return <ModalComponent {...props} isVisible={isVisible} setIsVisible={setIsVisible} />;
});

Modal.displayName = 'Modal';
